import type { AxiosRequestConfig } from "axios";
import api, { BACKEND_URL } from "./apiClient";
import { getSessionStorage } from "./apiViewAs";

export const FORCE_LOGIN_PROMPT_AFTER_LOGOUT_TTL_MS = 5 * 60 * 1000;
const FORCE_LOGIN_PROMPT_AFTER_LOGOUT_KEY = "force_login_prompt_after_logout_at";
const AUTH_CALLBACK_AUTO_RECOVERY_KEY = "auth_callback_auto_recovery_attempted";

export function buildApiUrl(path: string): string {
  return new URL(path, BACKEND_URL || window.location.origin).toString();
}

export function buildFrontendAuthRedirectUri(): string {
  return window.location.origin;
}

export function markForceLoginPromptAfterLogout(timestamp: number = Date.now()): void {
  getSessionStorage()?.setItem(FORCE_LOGIN_PROMPT_AFTER_LOGOUT_KEY, String(timestamp));
}

export function clearForceLoginPromptAfterLogout(): void {
  getSessionStorage()?.removeItem(FORCE_LOGIN_PROMPT_AFTER_LOGOUT_KEY);
}

export function markAuthCallbackAutoRecoveryAttempt(): void {
  getSessionStorage()?.setItem(AUTH_CALLBACK_AUTO_RECOVERY_KEY, "1");
}

export function hasAuthCallbackAutoRecoveryAttempt(): boolean {
  return getSessionStorage()?.getItem(AUTH_CALLBACK_AUTO_RECOVERY_KEY) === "1";
}

export function clearAuthCallbackAutoRecoveryAttempt(): void {
  getSessionStorage()?.removeItem(AUTH_CALLBACK_AUTO_RECOVERY_KEY);
}

export function shouldForceLoginPromptAfterLogout(now: number = Date.now()): boolean {
  const storage = getSessionStorage();
  if (storage === null) return false;
  const storedTimestamp = storage.getItem(FORCE_LOGIN_PROMPT_AFTER_LOGOUT_KEY);
  if (storedTimestamp === null || storedTimestamp === "") return false;
  const timestamp = Number.parseInt(storedTimestamp, 10);
  const isFresh =
    Number.isFinite(timestamp) &&
    now >= timestamp &&
    now - timestamp <= FORCE_LOGIN_PROMPT_AFTER_LOGOUT_TTL_MS;
  if (!isFresh) clearForceLoginPromptAfterLogout();
  return isFresh;
}

interface AuthFlowUrlOptions {
  prompt?: string;
}

function buildAuthFlowUrl(
  path: string,
  redirectUri: string = buildFrontendAuthRedirectUri(),
  options: AuthFlowUrlOptions = {},
): string {
  const url = new URL(path, BACKEND_URL || buildFrontendAuthRedirectUri());
  url.searchParams.set("redirect_uri", redirectUri);
  if (typeof options.prompt === "string" && options.prompt !== "") {
    url.searchParams.set("prompt", options.prompt);
  }
  return url.toString();
}

export function buildAuthLoginUrl(
  redirectUri: string = buildFrontendAuthRedirectUri(),
  options: AuthFlowUrlOptions = {},
): string {
  return buildAuthFlowUrl("/api/v2/auth/login", redirectUri, options);
}

export function buildNextAuthLoginUrl(
  redirectUri: string = buildFrontendAuthRedirectUri(),
): string {
  return shouldForceLoginPromptAfterLogout()
    ? buildAuthLoginUrl(redirectUri, { prompt: "login" })
    : buildAuthLoginUrl(redirectUri);
}

export function buildAuthLogoutUrl(redirectUri: string = buildFrontendAuthRedirectUri()): string {
  return buildAuthFlowUrl("/api/v2/auth/logout", redirectUri);
}

export function replaceBrowserLocation(url: string): void {
  window.location.replace(url);
}

function normalizeRequestPath(url: unknown): string {
  if (typeof url !== "string" || url.trim() === "") return "";
  try {
    return new URL(url, BACKEND_URL || window.location.origin).pathname;
  } catch {
    return url;
  }
}

/**
 * A 401 from the API means the session cookie no longer resolves to a user;
 * the auth endpoints report their own session state and are never retried.
 */
function isSessionRecoverableRequest(url: unknown): boolean {
  const path = normalizeRequestPath(url);
  return path.startsWith("/api/") && !path.startsWith("/api/v2/auth/");
}

/** A refresh answering this rejected the session and cleared its cookies. */
function isRejectedRefresh(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403;
}

/**
 * 401s arriving shortly after a refresh settled belong to the same episode
 * (requests sent with the old cookie); they reuse its outcome instead of
 * refreshing again.
 */
const AUTH_REFRESH_EPISODE_MS = 5_000;

export interface AuthRecoveryHandlers {
  clearAuthState?: () => void;
  isAuthenticated?: () => boolean;
  synchronizeAuthState?: (options: { skipAuthRecovery: boolean }) => Promise<unknown> | unknown;
}

async function recoverAuthenticatedSession({
  clearAuthState,
  synchronizeAuthState,
}: Pick<AuthRecoveryHandlers, "clearAuthState" | "synchronizeAuthState">): Promise<boolean> {
  try {
    await api.post("/api/v2/auth/refresh", undefined, { skipAuthRecovery: true });
    await synchronizeAuthState?.({ skipAuthRecovery: true });
    return true;
  } catch (error) {
    // An outage keeps the user signed in; only a rejected session signs out.
    if (isRejectedRefresh(error)) clearAuthState?.();
    return false;
  }
}

/**
 * Refreshes the session once per 401 episode and retries the request once.
 * A 403 is a permission decision, not an expired session: it is never
 * refreshed or retried. When the refresh is rejected, or the retried request
 * is still unauthorized, auth state is cleared so the dashboard routes to
 * login and authenticated pollers stop.
 */
export function installAuthRecoveryInterceptor({
  clearAuthState,
  isAuthenticated,
  synchronizeAuthState,
}: AuthRecoveryHandlers): () => void {
  if (!api?.interceptors?.response || typeof api.interceptors.response.use !== "function") {
    return () => {};
  }
  let episode: { outcome: Promise<boolean>; settledAt: number | null } | null = null;
  const refreshOnce = (): Promise<boolean> => {
    if (
      episode !== null &&
      (episode.settledAt === null || Date.now() - episode.settledAt < AUTH_REFRESH_EPISODE_MS)
    ) {
      return episode.outcome;
    }
    const current: { outcome: Promise<boolean>; settledAt: number | null } = {
      outcome: Promise.resolve(false),
      settledAt: null,
    };
    current.outcome = recoverAuthenticatedSession({ clearAuthState, synchronizeAuthState }).then(
      (recovered) => {
        current.settledAt = Date.now();
        return recovered;
      },
    );
    episode = current;
    return current.outcome;
  };
  const interceptorId = api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config: AxiosRequestConfig = error?.config ?? {};
      if (
        error?.response?.status !== 401 ||
        !isSessionRecoverableRequest(config.url) ||
        config.skipAuthRecovery === true ||
        typeof isAuthenticated !== "function" ||
        !isAuthenticated()
      ) {
        return Promise.reject(error);
      }
      if (config.__authRecoveryRetried === true) {
        // The refreshed session was rejected too: it cannot be recovered.
        clearAuthState?.();
        return Promise.reject(error);
      }
      const recovered = await refreshOnce();
      if (!recovered) return Promise.reject(error);
      return api.request({ ...config, __authRecoveryRetried: true });
    },
  );
  return () => {
    if (typeof api.interceptors.response.eject === "function") {
      api.interceptors.response.eject(interceptorId);
    }
  };
}
